/**
 * Assistant de choix — interface.
 *
 * Le panneau ne s'affiche que si un service est configuré : un bouton d'aide
 * qui ne répond pas vaut moins que pas de bouton du tout.
 *
 * Le véhicule déjà renseigné sur le site est joint à chaque question — le
 * visiteur n'a pas à le redire. Rien d'autre ne quitte le navigateur.
 *
 * Les garde-fous vivent sur le serveur (vision-endpoint/assistant.js) : ce
 * fichier ne fait que présenter. Il ne doit RIEN affirmer de lui-même.
 */
(() => {
  "use strict";

  const MEMOIRE = "carplay-lyon:vehicle:v1";

  const echapper = (valeur) =>
    String(valeur).replace(/[&<>"']/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

  const vehicule = () => {
    try {
      const brut = localStorage.getItem(MEMOIRE);
      if (!brut) return null;
      const v = JSON.parse(brut);
      return v && v.label ? { label: v.label, systemName: v.systemName || "" } : null;
    } catch (e) {
      return null;
    }
  };

  const monter = (racine) => {
    if (racine.dataset.monte) return;
    racine.dataset.monte = "true";

    const endpoint = String(racine.dataset.endpoint || "").replace(/\/+$/, "");
    if (!endpoint) return;

    const panneau = racine.querySelector("[data-assistant-panneau]");
    const bascule = racine.querySelector("[data-assistant-bascule]");
    const fil = racine.querySelector("[data-assistant-fil]");
    const form = racine.querySelector("[data-assistant-form]");
    const champ = racine.querySelector("[data-assistant-champ]");
    const envoyer = racine.querySelector("[data-assistant-envoyer]");

    racine.hidden = false;
    let messages = [];
    let occupe = false;

    const bulle = (role, texte) => {
      const li = document.createElement("li");
      li.className = role === "user" ? "flex justify-end" : "flex justify-start";
      li.innerHTML = `<p class="max-w-[85%] whitespace-pre-line rounded-[--radius-md] px-3.5 py-2.5 text-sm leading-relaxed ${
        role === "user"
          ? "bg-electric text-on-electric"
          : "border border-line/45 bg-surface/60 text-subtle"
      }">${echapper(texte)}</p>`;
      fil.appendChild(li);
      fil.scrollTop = fil.scrollHeight;
      return li;
    };

    const ouvrir = (etat) => {
      panneau.hidden = !etat;
      bascule.setAttribute("aria-expanded", String(etat));
      if (etat) {
        if (!messages.length) {
          const v = vehicule();
          bulle("assistant", v
            ? `Bonjour. Je vois que vous avez renseigné une ${v.label}. Que puis-je vous expliquer — les solutions possibles, les prix, la pose ?`
            : "Bonjour. Dites-moi ce que vous cherchez : activer CarPlay sur votre écran d'origine, un fichier seul, un autoradio ? Je vous oriente.");
        }
        champ.focus();
      }
    };

    bascule.addEventListener("click", () => ouvrir(panneau.hidden));
    racine.querySelector("[data-assistant-fermer]").addEventListener("click", () => {
      ouvrir(false);
      bascule.focus();
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !panneau.hidden) ouvrir(false);
    });

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      const question = champ.value.trim();
      if (!question || occupe) return;

      champ.value = "";
      messages.push({ role: "user", content: question });
      bulle("user", question);

      occupe = true;
      envoyer.disabled = true;
      const attente = bulle("assistant", "…");

      const controleur = new AbortController();
      const minuteur = setTimeout(() => controleur.abort(), 30000);
      try {
        const reponse = await fetch(`${endpoint}/assistant`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: controleur.signal,
          body: JSON.stringify({ messages: messages.slice(-8), vehicule: vehicule() }),
        });
        const data = await reponse.json();
        const texte = data.reponse
          || "Je n'ai pas pu répondre. Écrivez-nous, nous répondons avec votre configuration sous les yeux.";
        attente.remove();
        bulle("assistant", texte);
        messages.push({ role: "assistant", content: texte });
      } catch (erreur) {
        attente.remove();
        bulle("assistant", erreur.name === "AbortError"
          ? "Je mets trop de temps. Reformulez, ou écrivez-nous directement."
          : "Je n'arrive pas à répondre pour l'instant. Écrivez-nous, nous prenons le relais.");
      } finally {
        clearTimeout(minuteur);
        occupe = false;
        envoyer.disabled = false;
        champ.focus();
      }
    });
  };

  const demarrer = () => document.querySelectorAll("[data-assistant]").forEach(monter);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", demarrer, { once: true });
  } else {
    demarrer();
  }
  document.addEventListener("shopify:section:load", demarrer);
})();
